import { useState } from "react";
import { useGetIdentity, useNotify } from "ra-core";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { LossDialog, type LossTarget } from "../pipeline/LossDialog";
import { OUTCOMES } from "../myday/logActivity";
import { useOffers } from "../useOffers";
import {
  resolveVisitTarget,
  saveVisit,
  type VisitStop,
  type VisitTarget,
} from "./visitReport";

/**
 * Reporte de visita (PLAN_RUTAS.md §C): resultado + nota → Guardar. Reusa los mismos
 * resultados y `logActivity` que el registro rápido, con canal 'visita'. Si la parada
 * todavía no es empresa del CRM, se promueve sola al guardar (oferta elegida sin preguntar).
 */
export const VisitReport = ({
  stop,
  stopName,
  preferredSlug,
  onClose,
  onSaved,
}: {
  stop: VisitStop;
  stopName: string;
  preferredSlug: string | null;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const [outcome, setOutcome] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [target, setTarget] = useState<VisitTarget | null>(null);
  const [loss, setLoss] = useState<LossTarget | null>(null);
  const { data: offers } = useOffers();
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const queryClient = useQueryClient();

  const finish = async (t: VisitTarget) => {
    if (!outcome) return;
    await saveVisit(t, outcome, note);
    notify("Visita registrada", { type: "success" });
    ["my_day", "deal_board", "funnel_by_offer", "weekly_goal"].forEach((k) =>
      queryClient.invalidateQueries({ queryKey: [k] }),
    );
    onSaved();
  };

  const run = async (resolved?: VisitTarget) => {
    if (!outcome || !offers) return;
    setSaving(true);
    try {
      const t =
        resolved ??
        (await resolveVisitTarget({
          stop,
          offers,
          preferredSlug,
          salesId: Number(identity?.id),
        }));
      // "No interesado": primero el motivo de pérdida; si cancela, no se guarda nada
      if (outcome === "no_interesado" && t.deal && !resolved) {
        setTarget(t);
        setLoss({
          id: t.deal.id,
          key: t.deal.key,
          company_name: t.companyName,
          offer_id: t.deal.offer_id,
        });
        return;
      }
      await finish(t);
    } catch (e) {
      notify((e as Error).message, { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <LossDialog
        target={loss}
        onClose={(lost) => {
          setLoss(null);
          if (lost && target) run(target);
        }}
      />
      <Sheet
        open
        onOpenChange={(open) => {
          if (!open && !saving) onClose();
        }}
      >
        <SheetContent side="bottom" className="max-h-[90vh] overflow-y-auto">
          <SheetHeader className="pb-0">
            <SheetTitle>Reporte de visita</SheetTitle>
            <SheetDescription>{stopName}</SheetDescription>
          </SheetHeader>
          <div className="flex flex-col gap-4 px-4 pb-4">
            <div className="grid grid-cols-2 gap-2">
              {OUTCOMES.map((o) => (
                <Button
                  key={o.value}
                  variant={outcome === o.value ? "default" : "outline"}
                  className="h-12 whitespace-normal leading-tight"
                  onClick={() => setOutcome(o.value)}
                >
                  {o.label}
                </Button>
              ))}
            </div>
            <Textarea
              placeholder="Nota (opcional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
            />
            <Button
              className="h-14 text-base"
              disabled={!outcome || !offers || saving}
              onClick={() => run()}
            >
              {saving ? "Guardando…" : "Guardar visita"}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
